import LoadingButton from '@mui/lab/LoadingButton';
import { m } from 'framer-motion';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { RHFMultiSelect, RHFSelect, RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import RHFTextArea from 'src/components/hook-form/rhf-text-area';
import {
  RHFUploadMultiFile,
  RHFUploadSingleFileAccelerator,
} from 'src/components/hook-form/rhf-upload';
import { useEventsStore } from 'src/store/benefits/events.store';
import { useMyDataStore } from 'src/store/me/me.store';
import { useEffect, useState, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { DatePicker } from '@mui/x-date-pickers';
import { format, parseISO } from 'date-fns';
import Iconify from 'src/components/iconify';
import { useResponsive } from 'src/hooks/use-responsive';

type tier = {
  value: string;
  label: string;
};

type SectionKey = 'display' | 'info' | 'rules' | 'feedback';

const formatDateForPeriodTooltip = (value: unknown) => {
  if (typeof value !== 'string' || !value) {
    return '';
  }

  const parsedDate = parseISO(value);

  return Number.isNaN(parsedDate.getTime()) ? '' : format(parsedDate, 'dd/MM/yyyy');
};

function SectionTitle({ index, title }: { index: number; title: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'common.white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
        }}
      >
        {index}
      </Box>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
  );
}

type Props = {
  methods: any;
  onSubmit: VoidFunction;
  setOpen: (v: boolean) => void;
  setOpenCpfs: (v: boolean) => void;
  cpfs: string[];
  days: any[];
  handleDrop: any;
  handleDropMultiple: any;
  handleRemove: any;
  handleRemoveAll: any;
  values: any;
  isEdit: boolean;
  isSubmitting: boolean;
  tierOptions: tier[];
  categoriesOptions: tier[];
  scheduleOptions: string[];
  schedulelabel: string[];
  privateOptions: boolean[];
  privatelabel: string[];
  limitsOptions: boolean[];
  limitslabel: string[];
  dontOptions: boolean[];
  dontlabel: string[];
  ageOptions: tier[];
  optionsHighlight: tier[];
  exhibitionEnvOptions: tier[];
  hasCpfsToDownload: boolean;
  hasDaysToDownload: boolean;
  handleDownloadCpfs: VoidFunction;
  handleDownloadDays: VoidFunction;
};

export default function Eventform({
  methods,
  onSubmit,
  setOpen,
  setOpenCpfs,
  cpfs,
  days,
  handleDrop,
  handleDropMultiple,
  handleRemove,
  handleRemoveAll,
  values,
  isEdit,
  isSubmitting,
  tierOptions,
  categoriesOptions,
  scheduleOptions,
  schedulelabel,
  limitsOptions,
  limitslabel,
  ageOptions,
  optionsHighlight,
  privateOptions,
  privatelabel,
  dontOptions,
  dontlabel,
  exhibitionEnvOptions,
  hasCpfsToDownload,
  hasDaysToDownload,
  handleDownloadCpfs,
  handleDownloadDays,
}: Props) {
  const { eventDetails } = useEventsStore();
  const permissions = useMyDataStore((state) => state.me?.cnAdminInfo?.permissions?.benefits);
  const theme = useTheme();
  const lgUp = useResponsive('up', 'lg');
  const hasCustomerVoice = useWatch({ control: methods.control, name: 'hasCustomerVoice' });
  const [customerVoiceEmbedScript, setCustomerVoiceEmbedScript] = useState('');
  const [isCustomerVoiceScriptDialogOpen, setIsCustomerVoiceScriptDialogOpen] = useState(false);
  const [isScriptInstructionsPreviewOpen, setIsScriptInstructionsPreviewOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    display: true,
    info: true,
    rules: true,
    feedback: true,
  });
  const scriptInstructionsPreviewAnchorRef = useRef<HTMLDivElement | null>(null);
  const submitActionAnchorRef = useRef<HTMLDivElement | null>(null);
  const submitActionBarRef = useRef<HTMLDivElement | null>(null);
  const [isSubmitActionPinned, setIsSubmitActionPinned] = useState(false);
  const [submitActionFrame, setSubmitActionFrame] = useState({ left: 0, width: 0, height: 0 });
  const submitFloatingBottom = lgUp ? 24 : 16;
  const isTrailEnv = values.exhibitionEnv === 'trail';
  const startDatePeriodLabel = formatDateForPeriodTooltip(values.startDate);
  const endDatePeriodLabel = formatDateForPeriodTooltip(values.endDate);
  const hasCompletePeriod = Boolean(startDatePeriodLabel && endDatePeriodLabel);
  const durationLabel =
    {
      day: 'Duração da experiência (em dias)',
      hour: 'Duração da experiência (em minutos)',
    }[values.scheduleType as string] || 'Duração';

  useEffect(() => {
    const updateSubmitActionFrame = () => {
      if (!submitActionAnchorRef.current || !submitActionBarRef.current) {
        return;
      }

      const anchorRect = submitActionAnchorRef.current.getBoundingClientRect();
      const nextFrame = {
        left: Math.round(anchorRect.left),
        width: Math.round(anchorRect.width),
        height: Math.round(submitActionBarRef.current.getBoundingClientRect().height),
      };
      const shouldBePinned = anchorRect.bottom > window.innerHeight - submitFloatingBottom;

      setIsSubmitActionPinned((current) => (current === shouldBePinned ? current : shouldBePinned));
      setSubmitActionFrame((current) =>
        current.left === nextFrame.left &&
          current.width === nextFrame.width &&
          current.height === nextFrame.height
          ? current
          : nextFrame
      );
    };

    updateSubmitActionFrame();

    window.addEventListener('scroll', updateSubmitActionFrame, { passive: true });
    window.addEventListener('resize', updateSubmitActionFrame);

    return () => {
      window.removeEventListener('scroll', updateSubmitActionFrame);
      window.removeEventListener('resize', updateSubmitActionFrame);
    };
  }, [submitFloatingBottom]);

  const toggleSection = (section: SectionKey) => {
    setExpandedSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const applyCustomerVoiceEmbedScript = (rawScript: string) => {
    const script = rawScript.trim();

    if (!script) {
      return false;
    }

    const surveyEmbedMatch = script.match(
      /new\s+SurveyEmbed\s*\(\s*['"]([^'"]+)['"]\s*,\s*['"]([^'"]+)['"]\s*,\s*['"]([^'"]+)['"]/i
    );

    const formId = surveyEmbedMatch?.[1]?.trim();
    const baseUrl = surveyEmbedMatch?.[2]?.trim();
    const assetsUrl = surveyEmbedMatch?.[3]?.trim();

    if (!formId && !baseUrl && !assetsUrl) {
      return false;
    }

    methods.setValue('hasCustomerVoice', true, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (baseUrl) {
      methods.setValue('customerVoiceData.baseUrl', baseUrl, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (assetsUrl) {
      methods.setValue('customerVoiceData.assetsUrl', assetsUrl, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (formId) {
      methods.setValue('customerVoiceData.customerVoiceFormId', formId, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    return true;
  };

  const renderConditionalReveal = (condition: boolean, content: ReactNode) => (
    <Collapse in={condition} timeout={{ enter: 280, exit: 200 }} unmountOnExit>
      <Box
        sx={{
          pt: 0.5,
          opacity: condition ? 1 : 0,
          transform: condition ? 'translateY(0)' : 'translateY(-4px)',
          transition: 'opacity 220ms ease, transform 220ms ease',
        }}
      >
        {content}
      </Box>
    </Collapse>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={3}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={1}
          sx={{ p: 1 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Ativo
          </Typography>
          <Controller
            name="status"
            control={methods.control}
            render={({ field }) => (
              <Switch
                checked={field.value === 'active'}
                onChange={(_, checked) => field.onChange(checked ? 'active' : 'inactive')}
              />
            )}
          />
        </Stack>

        <Card>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <SectionTitle index={1} title="Exibição" />
              <IconButton onClick={() => toggleSection('display')}>
                {expandedSections.display ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Stack>

            <Collapse in={expandedSections.display} timeout="auto" unmountOnExit={false}>
              <Stack spacing={3} sx={{ pt: 0.5 }}>
                <RHFSelect
                  name="exhibitionEnv"
                  label="Ambiente de exibição"
                  variant="outlined"
                  sx={{
                    width: '100%',
                    borderRadius: '3px',
                  }}
                  disabled={isEdit}
                >
                  {exhibitionEnvOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </RHFSelect>

                {!isTrailEnv && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      backgroundColor: theme.palette.grey[100],
                    }}
                  >
                    <Stack spacing={2}>
                      <Typography variant="body2" fontWeight={600}>
                        Tipo de exibição
                      </Typography>
                      <RHFRadioGroupLegacy
                        name="private"
                        options={privateOptions}
                        getOptionLabel={privatelabel}
                      />

                      {renderConditionalReveal(
                        !!values.private && values.private !== 'false',
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.300',
                            backgroundColor: theme.palette.grey[100],
                          }}
                        >
                          <Stack spacing={2}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              sx={{
                                justifyContent: 'flex-start',
                                alignItems: { xs: 'flex-start', sm: 'center' },
                              }}
                              spacing={2}
                            >
                              <Button variant="soft" onClick={() => setOpenCpfs(true)}>
                                Cadastrar Cpfs
                              </Button>
                              {hasCpfsToDownload && (
                                <Button
                                  variant="soft"
                                  startIcon={<Iconify icon="solar:download-linear" width={18} />}
                                  onClick={handleDownloadCpfs}
                                >
                                  Baixar planilha
                                </Button>
                              )}
                              <Typography variant="overline">
                                {cpfs?.length || 0} Cpfs Cadastrados
                              </Typography>
                            </Stack>

                            <Stack>
                              <Typography variant="body1" fontWeight="bold">
                                Não mostrar para:
                              </Typography>
                              <RHFRadioGroupLegacy
                                name="dontShowToImported"
                                options={dontOptions}
                                getOptionLabel={dontlabel}
                              />
                            </Stack>
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}

                <Controller
                  name="test"
                  control={methods.control}
                  render={({ field }) => (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        backgroundColor: theme.palette.grey[100],
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        Benefício para teste
                      </Typography>
                      <Switch
                        checked={Boolean(field.value)}
                        onChange={(_, checked) => field.onChange(checked)}
                      />
                    </Box>
                  )}
                />

                {/* <RHFMultiSelect
                  name="highlightCategory"
                  options={optionsHighlight}
                  label="Selecionar Destaque"
                  chip
                  checkbox
                /> */}

                {!isTrailEnv && (
                  <Controller
                    name="visibleForTiers"
                    control={methods.control}
                    render={({ field, fieldState: { error } }) => {
                      const selectedValues = Array.isArray(field.value) ? field.value : [];

                      return (
                        <Stack spacing={1}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            Exibir para tiers
                          </Typography>

                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {tierOptions.map((option) => {
                              const isSelected = selectedValues.includes(option.value);

                              return (
                                <Chip
                                  key={option.value}
                                  label={option.label}
                                  clickable
                                  onClick={() => {
                                    if (option.value === 'all') {
                                      field.onChange(
                                        isSelected
                                          ? selectedValues.filter((value) => value !== 'all')
                                          : ['all']
                                      );
                                      return;
                                    }

                                    if (isSelected) {
                                      field.onChange(
                                        selectedValues.length === 1
                                          ? ['all']
                                          : selectedValues.filter((value) => value !== option.value)
                                      );
                                      return;
                                    }

                                    field.onChange(
                                      selectedValues.length === 2
                                        ? ['all']
                                        : [
                                          ...selectedValues.filter((value) => value !== 'all'),
                                          option.value,
                                        ]
                                    );
                                  }}
                                  color={isSelected ? 'primary' : 'default'}
                                  variant={isSelected ? 'filled' : 'outlined'}
                                  sx={{
                                    fontWeight: 600,
                                    color: isSelected ? 'common.white' : 'text.primary',
                                    '& .MuiChip-label': {
                                      color: isSelected ? 'common.white' : 'inherit',
                                    },
                                    '&:hover': {
                                      backgroundColor: isSelected ? 'primary.main' : 'action.hover',
                                      color: isSelected ? 'common.white' : 'text.primary',
                                    },
                                  }}
                                />
                              );
                            })}
                          </Stack>

                          {error ? (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                              {error.message}
                            </Typography>
                          ) : null}
                        </Stack>
                      );
                    }}
                  />
                )}

                <RHFSelect
                  name="ageRating"
                  label="Classificação Indicativa"
                  variant="outlined"
                  sx={{
                    width: '100%',
                    borderRadius: '3px',
                  }}
                >
                  {ageOptions?.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Stack>
            </Collapse>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <SectionTitle index={2} title="Informações" />
              <IconButton onClick={() => toggleSection('info')}>
                {expandedSections.info ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Stack>

            <Collapse in={expandedSections.info} timeout="auto" unmountOnExit={false}>
              <Stack spacing={3} sx={{ pt: 0.5 }}>
                <RHFTextField name="name" label="Nome" />

                <Stack spacing={1}>
                  <Typography variant="body1" fontWeight="bold">
                    Imagem:
                  </Typography>
                  <RHFUploadSingleFileAccelerator
                    name="image"
                    acceptAll={false}
                    onDrop={handleDrop}
                  />
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="body1" fontWeight="bold">
                    Banners:
                  </Typography>
                  <RHFUploadMultiFile
                    name="banners"
                    showPreview={false}
                    onRemove={handleRemove}
                    onRemoveAll={handleRemoveAll}
                    onDrop={handleDropMultiple}
                  />
                </Stack>

                <RHFTextArea name="description" label="Descrição" />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <RHFTextField name="local" label="Local da Experiência" />
                  <RHFMultiSelect
                    name="categories"
                    label="Categorias"
                    options={categoriesOptions}
                    chip
                    checkbox
                  />
                </Box>

                <RHFTextArea name="info" label="Informações" />
              </Stack>
            </Collapse>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <SectionTitle index={3} title="Regras" />
              <IconButton onClick={() => toggleSection('rules')}>
                {expandedSections.rules ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Stack>

            <Collapse in={expandedSections.rules} timeout="auto" unmountOnExit={false}>
              <Stack spacing={3} sx={{ pt: 0.5 }}>
                <Stack>
                  <Typography variant="body1" fontWeight="bold">
                    Tipo de Agendamento:
                  </Typography>
                  <RHFRadioGroupLegacy
                    name="scheduleType"
                    options={scheduleOptions}
                    getOptionLabel={schedulelabel}
                  />
                </Stack>

                {!isTrailEnv && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Controller
                      name="startDate"
                      control={methods.control}
                      render={({ field, fieldState: { error } }) => (
                        <DatePicker
                          label="Data de Início"
                          value={field.value ? parseISO(field.value) : null}
                          onChange={(date) =>
                            field.onChange(date ? format(date, 'yyyy-MM-dd') : '')
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!error,
                              helperText: error?.message,
                            },
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="endDate"
                      control={methods.control}
                      render={({ field, fieldState: { error } }) => (
                        <DatePicker
                          label="Data de Fim"
                          value={field.value ? parseISO(field.value) : null}
                          onChange={(date) =>
                            field.onChange(date ? format(date, 'yyyy-MM-dd') : '')
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!error,
                              helperText: error?.message,
                            },
                          }}
                        />
                      )}
                    />

                    {hasCompletePeriod && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ gridColumn: '1 / -1', mt: -1 }}
                      >
                        * Esta experiência começa no início do dia{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {startDatePeriodLabel}
                        </Box>{' '}
                        e termina no final do dia{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {endDatePeriodLabel}
                        </Box>
                        .
                      </Typography>
                    )}
                  </Box>
                )}

                {values.scheduleType === 'external' && (
                  <RHFTextField name="externalUrl" label="URL Externa" />
                )}

                {values.scheduleType !== 'external' && (
                  <Stack spacing={3}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 2,
                      }}
                    >
                      <RHFTextField
                        name="price"
                        label="Valor (em pontos)"
                        disabled={isTrailEnv}
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                        onKeyPress={(event) => {
                          if (!/[0-9]/.test(event.key)) {
                            event.preventDefault();
                          }
                        }}
                      />
                      {values.scheduleType !== 'simple' && (
                        <RHFTextField
                          name="duration"
                          label={durationLabel}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                          onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                              event.preventDefault();
                            }
                          }}
                        />
                      )}
                    </Box>

                    <Stack>
                      <Typography variant="body1" fontWeight="bold">
                        Possui limites:
                      </Typography>
                      <RHFRadioGroupLegacy
                        name="hasSlotLimits"
                        options={limitsOptions}
                        getOptionLabel={limitslabel}
                      />
                    </Stack>

                    {!!values.hasSlotLimits && values.hasSlotLimits !== 'false' && (
                      <Stack spacing={2}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Limites de participantes
                        </Typography>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 2,
                          }}
                        >
                          <RHFTextField
                            name="minAdult"
                            label="Adultos Mínimo"
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            onKeyPress={(event) => {
                              if (!/[0-9]/.test(event.key)) {
                                event.preventDefault();
                              }
                            }}
                          />
                          <RHFTextField
                            name="maxAdult"
                            label="Adultos Máximo"
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            onKeyPress={(event) => {
                              if (!/[0-9]/.test(event.key)) {
                                event.preventDefault();
                              }
                            }}
                          />
                          <RHFTextField
                            name="minChild"
                            label="Crianças Mínimo"
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            onKeyPress={(event) => {
                              if (!/[0-9]/.test(event.key)) {
                                event.preventDefault();
                              }
                            }}
                          />
                          <RHFTextField
                            name="maxChild"
                            label="Crianças Máximo"
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            onKeyPress={(event) => {
                              if (!/[0-9]/.test(event.key)) {
                                event.preventDefault();
                              }
                            }}
                          />
                          <RHFTextField
                            name="minTotal"
                            label="Total Mínimo"
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            onKeyPress={(event) => {
                              if (!/[0-9]/.test(event.key)) {
                                event.preventDefault();
                              }
                            }}
                          />
                          <RHFTextField
                            name="maxTotal"
                            label="Total Máximo"
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            onKeyPress={(event) => {
                              if (!/[0-9]/.test(event.key)) {
                                event.preventDefault();
                              }
                            }}
                          />
                        </Box>
                      </Stack>
                    )}
                  </Stack>
                )}

                {['day', 'hour'].includes(values.scheduleType) && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    sx={{
                      justifyContent: 'flex-start',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                    }}
                    spacing={2}
                  >
                    <Button variant="soft" onClick={() => setOpen(true)}>
                      Cadastrar Horários
                    </Button>
                    {hasDaysToDownload && (
                      <Button variant="soft" onClick={handleDownloadDays}>
                        Baixar planilha
                      </Button>
                    )}
                    <Typography variant="overline">
                      {days.length || eventDetails?.eventSlots?.length || 0} Horários Cadastrados
                    </Typography>
                  </Stack>
                )}

                {values.scheduleType === 'simple' && (
                  <RHFTextField
                    name="totalSlots"
                    label="Total de Vagas"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                  />
                )}

                <Divider />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <RHFTextField name="priority" label="Nível de prioridade" type="number" />
                  <RHFTextField
                    name="userPurchaseLimit"
                    label="Quantidade de resgates por usuário"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                  />
                </Box>
              </Stack>
            </Collapse>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <SectionTitle index={4} title="Formulário de Feedback" />
              <IconButton onClick={() => toggleSection('feedback')}>
                {expandedSections.feedback ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Stack>

            <Collapse in={expandedSections.feedback} timeout="auto" unmountOnExit={false}>
              <Stack spacing={3} sx={{ pt: 0.5 }}>
                <Controller
                  name="hasCustomerVoice"
                  control={methods.control}
                  render={({ field }) => (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        backgroundColor: theme.palette.grey[100],
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          Customer Voice
                        </Typography>
                        <Switch
                          checked={Boolean(field.value)}
                          onChange={(_, checked) => {
                            field.onChange(checked);

                            if (checked) {
                              return;
                            }

                            methods.setValue('customerVoiceData.baseUrl', '', {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                            methods.setValue('customerVoiceData.assetsUrl', '', {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                            methods.setValue('customerVoiceData.customerVoiceFormId', '', {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                        />
                      </Stack>

                      {renderConditionalReveal(
                        hasCustomerVoice,
                        <Stack spacing={2}>
                          <Stack spacing={2}>
                            <RHFTextField name="customerVoiceData.baseUrl" label="URL base" />
                            <RHFTextField
                              name="customerVoiceData.assetsUrl"
                              label="URL dos recursos"
                            />
                            <RHFTextField
                              name="customerVoiceData.customerVoiceFormId"
                              label="ID do formulário do Customer Voice"
                            />
                          </Stack>
                          <Stack direction="row" justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="outlined"
                              disableRipple
                              disableFocusRipple
                              disableTouchRipple
                              onClick={() => setIsCustomerVoiceScriptDialogOpen(true)}
                            >
                              Inserir script
                            </Button>
                          </Stack>
                        </Stack>
                      )}
                    </Box>
                  )}
                />

                <Dialog
                  open={isCustomerVoiceScriptDialogOpen}
                  onClose={() => setIsCustomerVoiceScriptDialogOpen(false)}
                  fullWidth
                  maxWidth="md"
                >
                  <DialogTitle>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                      <Typography variant="h6">Colar script do Customer Voice</Typography>
                      <Stack spacing={1} alignItems="flex-start">
                        <Stack direction="row" alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            Como conseguir o script
                          </Typography>
                          <Tooltip
                            arrow
                            placement="left"
                            title={
                              <Stack spacing={0.5} sx={{ py: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  Como obter o script (exemplo)
                                </Typography>
                                <Typography variant="caption">
                                  1. Acesse aba de inserção.
                                </Typography>
                                <Typography variant="caption">
                                  2. Escolher formato embutido.
                                </Typography>
                                <Typography variant="caption">
                                  3. Copie o script dentro do campo de código inserido (abaixo você
                                  pode ver uma imagem de exemplo).
                                </Typography>
                              </Stack>
                            }
                          >
                            <IconButton size="small" sx={{ p: 0.4 }}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Box
                          ref={scriptInstructionsPreviewAnchorRef}
                          component="img"
                          src="/assets/script_instructions.png"
                          alt="Exemplo de como localizar e copiar o script"
                          onMouseEnter={() => setIsScriptInstructionsPreviewOpen(true)}
                          onMouseLeave={() => setIsScriptInstructionsPreviewOpen(false)}
                          sx={{
                            width: 160,
                            maxWidth: '100%',
                            display: 'block',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'default',
                            boxShadow: 1,
                            bgcolor: 'background.paper',
                          }}
                        />
                      </Stack>
                    </Stack>
                  </DialogTitle>
                  <DialogContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Script embedado
                    </Typography>
                    {typeof document !== 'undefined' && isScriptInstructionsPreviewOpen
                      ? createPortal(
                        <Box
                          onMouseEnter={() => setIsScriptInstructionsPreviewOpen(true)}
                          onMouseLeave={() => setIsScriptInstructionsPreviewOpen(false)}
                          sx={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: (currentTheme) => currentTheme.zIndex.modal + 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/script_instructions.png"
                            alt="Exemplo ampliado de como localizar e copiar o script"
                            sx={{
                              width: 'min(92vw, 1200px)',
                              maxHeight: '88vh',
                              objectFit: 'contain',
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              boxShadow: (currentTheme) => currentTheme.shadows[24],
                              backgroundColor: 'background.paper',
                              pointerEvents: 'none',
                            }}
                          />
                        </Box>,
                        document.body
                      )
                      : null}
                    <TextField
                      autoFocus
                      fullWidth
                      multiline
                      minRows={6}
                      value={customerVoiceEmbedScript}
                      onChange={(event) => {
                        const nextScript = event.target.value;
                        setCustomerVoiceEmbedScript(nextScript);

                        const hasAutoCompleted = applyCustomerVoiceEmbedScript(nextScript);

                        if (hasAutoCompleted) {
                          setIsCustomerVoiceScriptDialogOpen(false);
                        }
                      }}
                      placeholder="Cole aqui o script completo para extrair os dados automaticamente"
                      helperText="Quando os dados forem extraídos com sucesso, este diálogo será fechado automaticamente."
                    />
                  </DialogContent>
                  <DialogActions sx={{ justifyContent: 'flex-end' }}>
                    <Button onClick={() => setIsCustomerVoiceScriptDialogOpen(false)}>
                      Cancelar
                    </Button>
                  </DialogActions>
                </Dialog>
              </Stack>
            </Collapse>
          </CardContent>
        </Card>

        <Box
          ref={submitActionAnchorRef}
          sx={{
            mt: 3,
            minHeight: isSubmitActionPinned ? `${submitActionFrame.height}px` : 'auto',
          }}
        >
          <Stack
            component={m.div}
            ref={submitActionBarRef}
            alignItems="flex-end"
            sx={{
              position: isSubmitActionPinned ? 'fixed' : 'relative',
              left: isSubmitActionPinned ? submitActionFrame.left : 'auto',
              width: isSubmitActionPinned ? submitActionFrame.width : '100%',
              bottom: isSubmitActionPinned ? submitFloatingBottom : 'auto',
              px: isSubmitActionPinned ? 1 : 0,
              zIndex: (currentTheme) => currentTheme.zIndex.appBar,
              transition: 'box-shadow 180ms ease, transform 180ms ease',
              animation: isSubmitActionPinned ? 'eventSubmitPinnedReveal 180ms ease-out' : 'none',
              '@keyframes eventSubmitPinnedReveal': {
                from: {
                  opacity: 0.92,
                  transform: 'translateY(6px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={(isEdit && !permissions?.update) || (!isEdit && !permissions?.create)}
            >
              {!isEdit ? 'Cadastrar Experiência' : 'Editar Experiência'}
            </LoadingButton>
          </Stack>
        </Box>
      </Stack>
    </FormProvider>
  );
}
